import React, { type ChangeEvent, type ReactElement, useState } from 'react'
import Dialog, { type DialogProps } from '@mui/material/Dialog'
import { Box, TextField, Typography, useTheme } from '@mui/material'
import { BUTTON_SIZE, BUTTON_VARIANTS } from 'common/ui/theme-button/ThemeButton'
import ThemeButton from 'common/ui/theme-button'

import styles from './DeclineVerificationDialog.module.scss'

interface BidDetailsDialogProps extends DialogProps {
  open: boolean
  title: string
  subtitle: string
  textInputName: string
  displayMonths?: boolean
  confirmButtonText: string
  declineButtonText: string
  className?: string
  confirmButtonAction: (reason: string, months?: string) => void
  onClose: () => void
}
const DeclineVerificationDialog: React.FC<BidDetailsDialogProps> = ({
  open,
  onClose,
  title,
  subtitle,
  textInputName,
  displayMonths = false,
  confirmButtonText,
  declineButtonText,
  className,
  confirmButtonAction,
  ...rest
}): ReactElement => {
  const { palette } = useTheme()
  const [reason, setReason] = useState('')
  const [months, setMonths] = useState('')

  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setReason(event.target.value.trimStart())
  }

  const handleMonthsFieldChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const regex = /^[1-9][0-9]*$/

    if (regex.test(event.target.value) || event.target.value === '') {
      setMonths(event.target.value.trimStart())
    }
  }

  const onCloseHandler = (): void => {
    setReason('')
    setMonths('')
    onClose()
  }

  const onSubmitHandler = (): void => {
    setReason('')
    setMonths('')
    confirmButtonAction(reason, months)
  }

  return (
    <Dialog open={open} onClose={onCloseHandler} {...rest}>
      <Box className={`${styles.container} ${className ?? ''}`}>
        <div className={styles.title}>
          <Typography variant="title7">{title}</Typography>
          <Typography variant="title5" sx={{ color: palette.gray.main, alignText: 'start' }}>
            {subtitle}
          </Typography>
        </div>
        <div className={styles.border} />
        <div className={styles.inputs}>
          <Typography variant="title3">{textInputName}</Typography>
          <TextField
            name="reason"
            value={reason}
            onChange={handleTextFieldChange}
            className={styles.infoWrapper}
            variant="outlined"
            placeholder=""
            multiline
            sx={{
              width: '100%',
              background: palette.blue700.main,
              '& .MuiInputBase-input': {
                color: palette.gray.main,
                fontSize: '16px',
                minHeight: '200px',
              },
            }}
          />
          {displayMonths && (
            <>
              <Typography variant="title3">
                Upišite broj meseci za koji vendor može opet da aplicira za verifikaciju, računajući od
                današnjeg dana
              </Typography>
              <div className={styles.monthsInput}>
                <TextField
                  name="months"
                  type="text"
                  value={months}
                  onChange={handleMonthsFieldChange}
                  variant="outlined"
                  placeholder=""
                  sx={{
                    maxWidth: '150px',
                    '& .MuiInputBase-input': {
                      color: palette.gray.main,
                      fontSize: '16px',
                      padding: '9px 2px',
                    },
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'center',
                      paddingLeft: '10px',
                      paddingRight: '10px',
                    },
                  }}
                />
              </div>
            </>
          )}
        </div>
        <div className={styles.buttonsContainer}>
          <ThemeButton size={BUTTON_SIZE.small} variant={BUTTON_VARIANTS.secondary} onClick={onCloseHandler}>
            {declineButtonText}
          </ThemeButton>
          <ThemeButton
            size={BUTTON_SIZE.small}
            disabled={(displayMonths && (!reason || !months)) || (!displayMonths && !reason)}
            onClick={onSubmitHandler}
          >
            {confirmButtonText}
          </ThemeButton>
        </div>
      </Box>
    </Dialog>
  )
}

export default DeclineVerificationDialog
